import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { imageFor } from "@/lib/imagery";
import type { RouteKey } from "@/lib/routes";
import { LANDING_CONTENT, LANDING_LABELS, type LandingContent } from "@/data/landingContent";
import { SEO } from "@/components/common/SEO";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";

interface LandingPageProps {
  routeKey: RouteKey;
  /** Optional related route keys shown at the bottom, before the CTA. */
  related?: RouteKey[];
}

/**
 * Full landing page composition used by every Service and Commodity page.
 * Layout, in order:
 *   Hero (editorial split) → Overview (2-col) → Capabilities grid →
 *   Industries → Trade Process → Positioning ("Why an Italian counterparty") →
 *   Related peer pages → Commercial CTA (dark)
 *
 * No animations, no fancy effects; pure typography + grid + one image
 * per page. Existing design-system tokens only.
 */
export function LandingPage({ routeKey, related = [] }: LandingPageProps) {
  const { t, path, locale } = useT();
  const bundle = LANDING_CONTENT[routeKey];
  const labels = LANDING_LABELS[locale];
  const content: LandingContent | undefined = bundle?.[locale];
  const image = imageFor(routeKey);
  const meta = t.routes[routeKey] as { title: string; eyebrow?: string; lede?: string };

  return (
    <>
      <SEO routeKey={routeKey} />

      {/* ── HERO ── editorial split, minimalist, image is large but never dominant */}
      <section
        className="border-b border-bone-500 pt-24 md:pt-28 pb-16 md:pb-20"
        data-testid={`landing-hero-${routeKey.replace(".", "-")}`}
      >
        <div className="container-bnm">
          <div className="mb-12">
            <Breadcrumbs current={routeKey} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              {meta.eyebrow && (
                <p className="eyebrow mb-6" data-testid="landing-eyebrow">
                  {meta.eyebrow}
                </p>
              )}
              <h1 className="font-display font-light text-4xl sm:text-5xl md:text-6xl lg:text-[68px] leading-[0.98] tracking-tight text-ink">
                {meta.title}
              </h1>
              {meta.lede && (
                <p className="mt-6 md:mt-8 text-lg md:text-xl text-slate2 max-w-prose-tight leading-relaxed">
                  {meta.lede}
                </p>
              )}
            </div>
            {image && (
              <div className="lg:col-span-5">
                <div className="aspect-[4/5] w-full overflow-hidden bg-ink/5">
                  <img
                    src={image.url}
                    srcSet={image.srcSet}
                    sizes="(min-width: 1024px) 40vw, 100vw"
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    loading="eager"
                    decoding="async"
                    width={800}
                    height={1000}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {content && (
        <>
          {/* ── 01 OVERVIEW ── quiet, editorial paragraph, no card */}
          <section className="border-b border-bone-500" aria-labelledby={`ov-${routeKey}`}>
            <div className="container-bnm py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <p className="eyebrow mb-4">{labels.overviewEyebrow}</p>
                <h2 id={`ov-${routeKey}`} className="font-display font-light text-3xl md:text-4xl text-ink leading-tight">
                  {labels.overviewLabel}
                </h2>
              </div>
              <div className="lg:col-span-8">
                <p className="font-display font-light text-2xl md:text-3xl leading-[1.25] text-ink max-w-[52ch]">
                  {content.overview}
                </p>
              </div>
            </div>
          </section>

          {/* ── 02 CAPABILITIES ── 2- or 3-column indexed grid */}
          <section className="border-b border-bone-500" aria-labelledby={`cap-${routeKey}`}>
            <div className="container-bnm py-20 md:py-28">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-16">
                <div className="lg:col-span-4">
                  <p className="eyebrow mb-4">{labels.capabilitiesEyebrow}</p>
                  <h2 id={`cap-${routeKey}`} className="font-display font-light text-3xl md:text-4xl text-ink leading-tight">
                    {labels.capabilitiesLabel}
                  </h2>
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-bone-500">
                {content.capabilities.map((c, idx) => (
                  <li
                    key={c.title}
                    className="border-r border-b border-bone-500 p-8 md:p-10 flex flex-col gap-4 bg-bone hover:bg-bone-300/40 transition-colors"
                    data-testid={`cap-${routeKey}-${idx}`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-eyebrow text-slate2">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-xl md:text-2xl text-ink leading-snug">
                      {c.title}
                    </h3>
                    <p className="text-slate2 leading-relaxed text-[15px]">
                      {c.body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ── 03 INDUSTRIES ── inline chip list */}
          <section className="border-b border-bone-500 bg-bone-300/40" aria-labelledby={`ind-${routeKey}`}>
            <div className="container-bnm py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <div className="lg:col-span-4">
                <p className="eyebrow mb-4">{labels.industriesEyebrow}</p>
                <h2 id={`ind-${routeKey}`} className="font-display font-light text-3xl md:text-4xl text-ink leading-tight">
                  {labels.industriesLabel}
                </h2>
                <p className="mt-6 text-xs uppercase tracking-eyebrow font-mono text-slate2">
                  {labels.industriesFootnote}
                </p>
              </div>
              <div className="lg:col-span-8">
                <ul className="flex flex-wrap gap-3">
                  {content.industries.map((label) => (
                    <li
                      key={label}
                      className="px-5 py-2.5 border border-ink text-ink text-sm md:text-[15px] tracking-tight bg-bone"
                    >
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ── 04 TRADE PROCESS ── numbered timeline, no icons */}
          <section className="border-b border-bone-500" aria-labelledby={`proc-${routeKey}`}>
            <div className="container-bnm py-20 md:py-28">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-16">
                <div className="lg:col-span-4">
                  <p className="eyebrow mb-4">{labels.processEyebrow}</p>
                  <h2 id={`proc-${routeKey}`} className="font-display font-light text-3xl md:text-4xl text-ink leading-tight">
                    {labels.processLabel}
                  </h2>
                  <p className="mt-6 text-xs uppercase tracking-eyebrow font-mono text-slate2 max-w-[38ch]">
                    {labels.processFootnote}
                  </p>
                </div>
                <div className="lg:col-span-8">
                  <ol className="border-t border-bone-500">
                    {content.process.map((step, idx) => (
                      <li
                        key={step.title}
                        className="border-b border-bone-500 py-6 md:py-7 grid grid-cols-12 gap-4 md:gap-8"
                        data-testid={`proc-${routeKey}-${idx}`}
                      >
                        <div className="col-span-2 md:col-span-1 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 pt-1">
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                        <div className="col-span-10 md:col-span-4">
                          <h3 className="font-display text-xl md:text-2xl text-ink leading-snug">
                            {step.title}
                          </h3>
                        </div>
                        <div className="col-span-12 md:col-span-7">
                          <p className="text-slate2 leading-relaxed text-[15px]">
                            {step.body}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* ── 05 WHY BNM ── positional attributes */}
          <section className="border-b border-bone-500 bg-ink text-bone" aria-labelledby={`why-${routeKey}`}>
            <div className="container-bnm py-20 md:py-28">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14 md:mb-16">
                <div className="lg:col-span-5">
                  <p className="eyebrow !text-bone/50 mb-4">{labels.whyEyebrow}</p>
                  <h2 id={`why-${routeKey}`} className="font-display font-light text-3xl md:text-4xl lg:text-5xl text-bone leading-tight">
                    {labels.whyLabel}
                  </h2>
                  <p className="mt-6 text-xs uppercase tracking-eyebrow font-mono text-bone/45 max-w-[40ch]">
                    {labels.whyFootnote}
                  </p>
                </div>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-bone/10">
                {content.whyBnm.map((w, idx) => (
                  <li
                    key={w.title}
                    className="bg-ink p-7 md:p-8 flex flex-col gap-4"
                    data-testid={`why-${routeKey}-${idx}`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-eyebrow text-clay-400">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-lg md:text-xl text-bone leading-snug">
                      {w.title}
                    </h3>
                    <p className="text-bone/65 text-sm leading-relaxed">
                      {w.body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* ── Related (peer pages) ── */}
      {related.length > 0 && (
        <section className="border-b border-bone-500">
          <div className="container-bnm py-16 md:py-20">
            <p className="eyebrow mb-8">{t.sections.seeAlso}</p>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-px bg-bone-500">
              {related.map((k) => (
                <li key={k} className="bg-bone">
                  <Link
                    to={path(k)}
                    className="group flex flex-col justify-between h-full p-6 md:p-8 hover:bg-bone-300/60 transition-colors"
                    data-testid={`related-${k.replace(".", "-")}`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-eyebrow text-slate2">
                      {(t.routes[k] as { eyebrow?: string }).eyebrow}
                    </span>
                    <h3 className="font-display text-2xl md:text-3xl text-ink group-hover:text-clay transition-colors mt-8">
                      {t.routes[k].label}
                    </h3>
                    <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-ink group-hover:text-clay transition-colors">
                      <ArrowRight size={14} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ── 06 CTA ── */}
      <section className="bg-clay text-bone" data-testid="landing-cta">
        <div className="container-bnm py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow !text-bone/70 mb-6">{labels.ctaEyebrow}</p>
            <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-bone">
              {t.home.ctaTitle}
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to={path("contact")}
              className="inline-flex items-center gap-3 bg-ink text-bone px-8 py-4 font-medium hover:bg-ink-900 transition-colors group"
              data-testid="landing-cta-btn"
            >
              {t.home.ctaAction}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
